"use client"

import { useForm, usePage } from "@inertiajs/react"
import { AlertCircle, Mail01 } from "@untitledui/icons"
import { useState } from "react"

import { Button } from "@/components/ui/button"
import { InputBase, TextField } from "@/components/ui/input"
import { Label } from "@/components/ui/input"
import {
  Dialog,
  DialogTrigger,
  Modal,
  ModalOverlay,
} from "@/components/ui/modal"
import { SectionFooter } from "@/components/ui/section-footer"
import { SectionHeader } from "@/components/ui/section-header"
import { SectionLabel } from "@/components/ui/section-label"
import SettingsLayout from "@/layouts/settings/layout"
import { settingsProfilePath, usersPath } from "@/routes"

interface ProfilePageProps {
  user: { id: number; name: string; email: string; avatar_url: string | null }
  [key: string]: unknown
}

export default function Profile() {
  const { user } = usePage<ProfilePageProps>().props
  const { data, setData, patch, processing, errors } = useForm({
    name: user.name,
  })

  const deleteAccountForm = useForm({
    password_challenge: "",
  })

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(settingsProfilePath())
  }

  const handleDeleteAccount = (e: React.FormEvent) => {
    e.preventDefault()
    deleteAccountForm.delete(usersPath(), {
      onSuccess: () => setIsDeleteModalOpen(false),
    })
  }

  return (
    <SettingsLayout>
      <form className="flex flex-col gap-6 px-4 lg:px-8" onSubmit={submit}>
        <SectionHeader.Root>
          <SectionHeader.Group>
            <div className="flex flex-1 flex-col justify-center gap-0.5 self-stretch">
              <SectionHeader.Heading>Personal info</SectionHeader.Heading>
              <SectionHeader.Subheading>
                Update your personal details here.
              </SectionHeader.Subheading>
            </div>

            <SectionHeader.Actions>
              <Button
                type="submit"
                color="primary"
                size="md"
                isDisabled={processing}
              >
                Save
              </Button>
            </SectionHeader.Actions>
          </SectionHeader.Group>
        </SectionHeader.Root>

        <div className="flex flex-col gap-5">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              isRequired
              size="sm"
              title="Name"
              className="max-lg:hidden"
            />

            <TextField
              isRequired
              name="name"
              value={data.name}
              onChange={(v) => setData("name", v)}
              isInvalid={!!errors.name}
              errorMessage={errors.name}
            >
              <Label className="lg:hidden">Name</Label>
              <InputBase size="md" />
            </TextField>
          </div>

          <hr className="bg-border-secondary h-px w-full border-none" />

          <div className="grid grid-cols-1 lg:grid-cols-[minmax(200px,280px)_minmax(400px,512px)] lg:gap-8">
            <SectionLabel.Root
              size="sm"
              title="Email address"
              className="max-lg:hidden"
            />

            <TextField isDisabled name="email" type="email" value={user.email}>
              <Label className="lg:hidden">Email address</Label>
              <InputBase size="md" icon={Mail01} />
            </TextField>
          </div>
        </div>

        <SectionFooter.Root>
          <SectionFooter.Actions>
            <Button
              type="submit"
              color="primary"
              size="md"
              isDisabled={processing}
            >
              Save
            </Button>
          </SectionFooter.Actions>
        </SectionFooter.Root>
      </form>

      <div className="mt-8 px-4 lg:px-8">
        <hr className="bg-border-secondary mb-8 h-px w-full border-none" />

        <div className="flex flex-col gap-4">
          <h2 className="text-error-primary text-lg font-semibold">
            Danger Zone
          </h2>

          <div className="border-error-subtle bg-error-50 flex flex-col items-start justify-between gap-4 rounded-xl border p-4 md:flex-row md:items-center">
            <div>
              <h3 className="text-error-primary text-sm font-semibold">
                Delete account
              </h3>
              <p className="text-error-secondary text-sm">
                Permanently delete your account and all of your content.
              </p>
            </div>

            <DialogTrigger
              isOpen={isDeleteModalOpen}
              onOpenChange={setIsDeleteModalOpen}
            >
              <Button color="primary-destructive" size="md">
                Delete account
              </Button>
              <ModalOverlay isDismissable>
                <Modal>
                  <Dialog className="mx-auto mt-20 flex w-full max-w-md flex-col gap-6 rounded-xl bg-white p-6 shadow-xl md:mt-0">
                    <div className="flex gap-4">
                      <div className="bg-error-100 text-error-600 h-fit shrink-0 rounded-full p-3">
                        <AlertCircle className="size-6" />
                      </div>
                      <div className="flex flex-col gap-2">
                        <h3 className="text-primary text-lg font-semibold">
                          Delete account
                        </h3>
                        <p className="text-tertiary text-sm">
                          Are you sure you want to delete your account? This
                          action cannot be undone.
                        </p>
                      </div>
                    </div>

                    <form
                      onSubmit={handleDeleteAccount}
                      className="flex flex-col gap-4"
                    >
                      <TextField
                        isRequired
                        name="password_challenge"
                        value={deleteAccountForm.data.password_challenge}
                        onChange={(v) =>
                          deleteAccountForm.setData("password_challenge", v)
                        }
                        isInvalid={
                          !!deleteAccountForm.errors.password_challenge
                        }
                        errorMessage={
                          deleteAccountForm.errors.password_challenge
                        }
                      >
                        <Label>Confirm password</Label>
                        <InputBase
                          type="password"
                          placeholder="Enter your password"
                          size="md"
                        />
                      </TextField>

                      <div className="mt-2 flex justify-end gap-3">
                        <Button
                          color="secondary"
                          onClick={() => setIsDeleteModalOpen(false)}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          color="primary-destructive"
                          isDisabled={deleteAccountForm.processing}
                        >
                          Delete account
                        </Button>
                      </div>
                    </form>
                  </Dialog>
                </Modal>
              </ModalOverlay>
            </DialogTrigger>
          </div>
        </div>
      </div>
    </SettingsLayout>
  )
}
